/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import {
  AsyncButton,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { RefObject } from "preact";
import { VNode, h } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { OperationError } from "./OperationError.js";

export interface WithdrawalDialogError {
  title: string;
  description: string;
}

export function AbortWithdrawalDialog({
  running,
  disabled,
  error,
  returnFocus,
  onKeep,
  onAbort,
}: {
  running: boolean;
  disabled: boolean;
  error?: WithdrawalDialogError;
  returnFocus?: RefObject<HTMLElement>;
  onKeep(): void;
  onAbort(): void | Promise<void>;
}): VNode {
  const { i18n } = useTranslationContext();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusTarget = returnFocus?.current;
    const preventCancel = (event: Event): void => event.preventDefault();
    dialog.addEventListener("cancel", preventCancel);
    let frame: number | undefined;
    const openWhenConnected = (): void => {
      if (!dialog.isConnected) {
        frame = requestAnimationFrame(openWhenConnected);
        return;
      }
      if (!dialog.open) dialog.showModal();
      titleRef.current?.focus();
    };
    frame = requestAnimationFrame(openWhenConnected);
    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      dialog.removeEventListener("cancel", preventCancel);
      if (dialog.open) dialog.close();
      focusTarget?.focus();
    };
  }, [returnFocus]);

  return (
    <dialog
      ref={dialogRef}
      aria-modal="true"
      aria-labelledby="abort-withdrawal-title"
      aria-describedby="abort-withdrawal-description"
      class="fixed inset-0 z-20 size-auto max-h-none max-w-none overflow-y-auto bg-transparent p-4 backdrop:bg-secondary/45"
    >
      <div class="flex min-h-full items-center justify-center">
        <section class="w-full max-w-md rounded-xl bg-white p-6 text-onBackground shadow-xl">
          <h2
            ref={titleRef}
            tabIndex={-1}
            id="abort-withdrawal-title"
            class="text-lg font-semibold outline-none"
          >
            <i18n.Translate>Abort this withdrawal?</i18n.Translate>
          </h2>
          <p
            id="abort-withdrawal-description"
            class="mt-3 text-sm text-gray-600"
          >
            <i18n.Translate>
              This stops the withdrawal. No money will be transferred, and this
              operation cannot be resumed.
            </i18n.Translate>
          </p>
          {error ? (
            <div
              class="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
              role="alert"
            >
              <div class="font-semibold">{error.title}</div>
              <p class="mt-1">{error.description}</p>
            </div>
          ) : undefined}
          <OperationError class="mt-4" />
          <div class="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              class="rounded-md px-3 py-2 text-sm font-semibold text-onBackground hover:bg-gray-100 disabled:cursor-default disabled:opacity-50"
              disabled={running}
              onClick={onKeep}
            >
              <i18n.Translate>Keep withdrawal</i18n.Translate>
            </button>
            <AsyncButton
              name="abort withdrawal"
              class="rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-800 disabled:cursor-default disabled:opacity-50"
              disabled={disabled}
              onClick={onAbort}
            >
              <i18n.Translate>Abort withdrawal</i18n.Translate>
            </AsyncButton>
          </div>
        </section>
      </div>
    </dialog>
  );
}
